import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

interface MediaUploaderProps {
  label: string;
  preview: string | null;
  mediaType: 'image' | 'video' | null;
  onMediaChange: (file: File | null, type: 'image' | 'video') => void;
  onRemove: () => void;
  allowVideos?: boolean; // Default false - videos disabled for now
  recommendedResolution?: string;
  maxImageSizeMB?: number;
}

const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm'];

export const MediaUploader = ({ label, preview, mediaType, onMediaChange, onRemove, allowVideos = false, recommendedResolution, maxImageSizeMB = 40 }: MediaUploaderProps) => {
  const MAX_IMAGE_SIZE = maxImageSizeMB * 1024 * 1024;
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const validateFile = (file: File): { valid: boolean; type: 'image' | 'video' } | null => {
    const isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);
    const isVideo = ACCEPTED_VIDEO_TYPES.includes(file.type);

    if (!isImage && (!allowVideos || !isVideo)) {
      toast({
        title: "Formato no válido",
        description: allowVideos 
          ? "Solo se permiten imágenes (JPG, PNG, WEBP, GIF) o videos (MP4, WEBM)"
          : "Solo se permiten imágenes (JPG, PNG, WEBP, GIF)",
        variant: "destructive"
      });
      return null;
    }

    const maxSize = isImage ? MAX_IMAGE_SIZE : MAX_VIDEO_SIZE;
    const maxSizeMB = maxSize / (1024 * 1024);

    if (file.size > maxSize) {
      toast({
        title: "Archivo muy grande",
        description: `El tamaño máximo es ${maxSizeMB}MB para ${isImage ? 'imágenes' : 'videos'}`,
        variant: "destructive"
      });
      return null;
    }

    return { valid: true, type: isImage ? 'image' : 'video' };
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      const validation = validateFile(file);
      if (validation) {
        onMediaChange(file, validation.type);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = validateFile(file);
      if (validation) {
        onMediaChange(file, validation.type);
      }
    }
    // Reset input value to allow selecting same file again
    e.target.value = '';
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold text-foreground">{label}</Label>
      
      {preview ? (
        <div className="relative group rounded-lg overflow-hidden border-2 border-border bg-muted h-64">
          {mediaType === 'video' ? (
            <video
              src={preview}
              className="w-full h-full object-cover"
              controls
              muted
              playsInline
            />
          ) : (
            <img
              src={preview}
              alt={label}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleClick}
              className="bg-white/90 hover:bg-white text-foreground"
            >
              <Upload className="h-4 w-4 mr-2" />
              Cambiar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onRemove}
              className="bg-destructive/90 hover:bg-destructive"
            >
              <X className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
          {/* Media type badge */}
          <div className="absolute top-2 left-2 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium flex items-center gap-1">
            {mediaType === 'video' ? (
              <>
                <Video className="h-3 w-3" />
                Video
              </>
            ) : (
              <>
                <ImageIcon className="h-3 w-3" />
                Imagen
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative h-64 rounded-lg border-2 border-dashed transition-all cursor-pointer
            flex flex-col items-center justify-center gap-3 p-6
            ${isDragging 
              ? 'border-primary bg-primary/5 scale-105' 
              : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }
          `}
        >
          <div className={`
            w-16 h-16 rounded-full flex items-center justify-center transition-colors
            ${isDragging ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
          `}>
            <div className="flex items-center gap-1">
              <ImageIcon className="h-6 w-6" />
              {allowVideos && <Video className="h-6 w-6" />}
            </div>
          </div>
          <div className="text-center">
            <p className="font-medium text-foreground mb-1">
              {isDragging ? 'Suelta el archivo aquí' : 'Haz clic o arrastra un archivo'}
            </p>
            <p className="text-sm text-muted-foreground">
              Imágenes: PNG, JPG, WEBP (máx {maxImageSizeMB}MB)
            </p>
            {allowVideos && (
              <p className="text-sm text-muted-foreground">
                Videos: MP4, WEBM (máx 50MB)
              </p>
            )}
            {recommendedResolution && (
              <p className="text-xs text-muted-foreground mt-1">
                Resolución recomendada: {recommendedResolution}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Seleccionar archivo
          </Button>
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept={allowVideos ? "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm" : "image/jpeg,image/png,image/webp,image/gif"}
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};
